/**
 * The case's member chain as a HORIZONTAL chip bar:
 *
 *     ✉ Inbound · Triaged │ ▣ Request C001-32-1 · Started │ ⬒ Report processing
 *
 * Most cases are short (inbound → request → report), so a horizontal
 * bar spends the space better than a rail column. Two modes:
 *
 * - **In-place selection** (the case page): pass `onSelect` — enquiry
 *   and request chips select the node in the page, and so does a report
 *   the caller may open in the workbench (`inPlaceReports`).
 * - **Navigation** (the report page, admin-only): omit `onSelect` —
 *   chips link into `/cases/{family}?item=…`.
 *
 * Every other report chip links to the report page, exactly as before;
 * the current node renders filled and inert.
 */

import { Link } from 'react-router-dom';

import { Badge, Group, Text } from '@mantine/core';
import { IconFileDescription, IconMail, IconReport } from '@tabler/icons-react';

import { CaseDetail, CaseItemRef } from '@/types/case';
import { formatStage } from '@/types/enquiry';
import { TriageStage, triageStageLabel } from '@/types/report-request';

export type CaseChainCurrent = CaseItemRef | null;

interface CaseChainProps {
  caseDetail: CaseDetail;
  current: CaseChainCurrent;
  /** In-place selection; when absent, chips navigate to the case page. */
  onSelect?: (ref: CaseItemRef) => void;
  /** Reports that open in the page (the workbench) instead of linking to
   * the report page. */
  inPlaceReports?: ReadonlySet<string>;
}

interface ChainNode {
  key: string;
  kind: 'inbound' | 'request' | 'report';
  typeLabel: string;
  label: string;
  isCurrent: boolean;
  /** In-place selection target (enquiry/request chips, workbench reports). */
  selectRef?: CaseItemRef;
  /** Navigation target when not selecting in place. */
  to: string;
  testId: string;
}

const CHIP_COLOR: Record<ChainNode['kind'], string> = {
  inbound: 'gray',
  request: 'blue',
  report: 'teal',
};

function chipIcon(kind: ChainNode['kind']) {
  switch (kind) {
    case 'inbound':
      return <IconMail size={11} />;
    case 'request':
      return <IconFileDescription size={11} />;
    case 'report':
      return <IconReport size={11} />;
  }
}

export function CaseChain({
  caseDetail,
  current,
  onSelect,
  inPlaceReports,
}: Readonly<CaseChainProps>) {
  const familyPath = `/cases/${encodeURIComponent(caseDetail.familyKey)}`;
  const nodes: ChainNode[] = [];
  const reportRef = (id: string): CaseItemRef | undefined =>
    inPlaceReports?.has(id) ? { type: 'report', id } : undefined;

  if (caseDetail.enquiry) {
    nodes.push({
      key: `enq-${caseDetail.enquiry.id}`,
      kind: 'inbound',
      typeLabel: 'Inbound',
      label: formatStage(caseDetail.enquiry.stage),
      isCurrent: current?.type === 'enquiry' && current.id === caseDetail.enquiry.id,
      selectRef: { type: 'enquiry', id: caseDetail.enquiry.id },
      to: `${familyPath}?item=enq:${caseDetail.enquiry.id}`,
      testId: `rail-enq-${caseDetail.enquiry.id}`,
    });
  }

  for (const rr of caseDetail.reportRequests) {
    nodes.push({
      key: `rr-${rr.id}`,
      kind: 'request',
      typeLabel: rr.evaluationOfReportId ? 'Evaluation copy' : 'Request',
      label: `${rr.codename ?? `${rr.id.slice(0, 8)}…`} · ${triageStageLabel(
        rr.triageStage as TriageStage
      )}`,
      isCurrent: current?.type === 'report_request' && current.id === rr.id,
      selectRef: { type: 'report_request', id: rr.id },
      to: `${familyPath}?item=rr:${rr.id}`,
      testId: `rail-rr-${rr.id}`,
    });
    if (rr.triageStage === 'started' && rr.reportId) {
      nodes.push({
        key: `rep-${rr.reportId}`,
        kind: 'report',
        typeLabel: 'Report',
        label: rr.reportLifecycleStage ? rr.reportLifecycleStage.replace(/_/g, ' ') : 'view',
        isCurrent: current?.type === 'report' && current.id === rr.reportId,
        selectRef: reportRef(rr.reportId),
        to: `/reports/${rr.reportId}`,
        testId: `rail-report-${rr.reportId}`,
      });
    }
  }

  for (const report of caseDetail.reports) {
    nodes.push({
      key: `rep-${report.id}`,
      kind: 'report',
      typeLabel: 'Report',
      label: report.lifecycleStage.replace(/_/g, ' '),
      isCurrent: current?.type === 'report' && current.id === report.id,
      selectRef: reportRef(report.id),
      to: `/reports/${report.id}`,
      testId: `rail-report-${report.id}`,
    });
  }

  if (nodes.length === 0) {
    return null;
  }

  return (
    <Group gap={6} wrap="wrap" data-testid="case-rail">
      {nodes.map((node, idx) => (
        <Group key={node.key} gap={6} wrap="nowrap">
          {idx > 0 && (
            <Text size="xs" c="dimmed">
              ·
            </Text>
          )}
          <ChainChip node={node} onSelect={onSelect} />
        </Group>
      ))}
    </Group>
  );
}

function ChainChip({
  node,
  onSelect,
}: Readonly<{ node: ChainNode; onSelect?: (ref: CaseItemRef) => void }>) {
  const content = (
    <>
      <Text component="span" size="xs" fw={400} opacity={0.75} tt="none">
        {node.typeLabel}{' '}
      </Text>
      {node.label}
    </>
  );
  const common = {
    size: 'sm' as const,
    radius: 'sm' as const,
    color: CHIP_COLOR[node.kind],
    leftSection: chipIcon(node.kind),
  };

  if (node.isCurrent) {
    return (
      <Badge {...common} variant="filled" data-testid={node.testId} aria-current="page">
        {content}
      </Badge>
    );
  }
  // Enquiry/request chips — and workbench reports — select in place when
  // the page offers it; every other report navigates.
  if (onSelect && node.selectRef) {
    const ref = node.selectRef;
    return (
      <Badge
        {...common}
        variant="light"
        component="button"
        type="button"
        style={{ cursor: 'pointer', border: 'none' }}
        onClick={() => onSelect(ref)}
        data-testid={node.testId}
      >
        {content}
      </Badge>
    );
  }
  return (
    <Badge
      {...common}
      variant="light"
      component={Link}
      to={node.to}
      style={{ cursor: 'pointer' }}
      data-testid={node.testId}
    >
      {content}
    </Badge>
  );
}
